import ItemList, { Item, } from "feature/ItemList"
import { mapClient, } from "globalInstance"
import React, { useCallback, useEffect, useState, } from "react"
import style from "./style.module.scss"
import { selectState, slice, } from "app/App/redux"
import { connect, ConnectedProps, useSelector, } from "react-redux"
import Menu, { MenuItemType, } from "feature/Menu"
import Navigator from "feature/Navigator"
import TextField from "@material-ui/core/TextField"
import { MapLocation, } from "indoor-map-lib"
import LocationCard from "feature/LocationCard"
import MappedinLocation from "indoor-map-lib/dist/models/Mappedin/MappedinLocation"
import MappedinCategory from "indoor-map-lib/dist/models/Mappedin/MappedinCategory"
import CloseIcon from "@material-ui/icons/Close"
import ArrowLeftIcon from "@material-ui/icons/ArrowLeft"
import IconButton from "@material-ui/core/IconButton"
import { fromEvent, Subscription, } from "rxjs"
import { debounceTime, map, switchMap, } from "rxjs/operators"

const mapDispatch = { ...slice.actions, }
const connector = connect(null, mapDispatch)
type Props = ConnectedProps<typeof connector>

// ソートの比較に使用するcompare用のインスタンス
const jaCollator = new Intl.Collator("ja")

// 検索テキストボックスのSubscription
let inputSubscriber: Subscription | null = null

/**
 * Menuコンポーネントと連動して情報を表示するためのコンポーネントです
 */
function ControlBoard ({
  setCurrentMenu,
  setCurrentLocation,
  setCurrentLevel,
  setSelectedCategory,
}: Props) {
  const {
    currentLocationId,
    currentMenu,
    selectedCategory,
  } = useSelector(selectState)
  const [items, setItems] = useState<Item[]>([])
  const [keyword, setKeyword] = useState("")
  const [selectedLocation, setSelectedLocation] = useState<MapLocation | null>(null)

  /**
   * 選択中のロケーション情報の詳細を取得して設定します
   */
  useEffect(() => {
    if (currentLocationId) {
      const location = mapClient.getLocations().find(location => {
        return (location.id === currentLocationId)
      }) || null
      setSelectedLocation(location)
    } else {
      setSelectedLocation(null)
    }
  }, [currentLocationId])

  /**
   * ロケーション情報のソートを行う関数
   */
  const sortLocationsByName = (a: MappedinLocation, b: MappedinLocation) => {
    return jaCollator.compare(a.name, b.name)
  }

  /**
   * カテゴリ情報のソートを行う関数
   */
  const sortCategoriesByName = (a: MappedinCategory, b: MappedinCategory) => {
    return jaCollator.compare(a.name, b.name)
  }

  /**
   * 施設メニュー時の一覧を取得
   */
  const getFacilityItems = useCallback(() => {
    if (!selectedCategory) return []

    // ロケーション一覧を取得
    const locations = mapClient.getLocationsByCategoryId(selectedCategory)
      .sort(sortLocationsByName).map(location => {
        const item: Item = {
          label: location.name,
          value: location.id,
        }
        return item
      })

    return locations
  }, [selectedCategory])

  /**
   * カテゴリ一覧を取得
   */
  const getCategoryItems = useCallback(() => {
    const categories = mapClient.getCategories().sort(sortCategoriesByName).map(category => {
      const item: Item = {
        label: category.name,
        value: category.id,
      }
      return item
    })

    return categories
  }, [])

  /**
   * 座席メニューの一覧を取得
   */
  const getSeatItems = useCallback(() => {
    // ロケーション一覧を取得して座席タイプで絞り込む
    const items = mapClient.getLocations().filter(location => {
      return (location.type === "seating")
    }).sort(sortLocationsByName).map((location) => {
      const item: Item = {
        label: location.name,
        value: location.id,
      }
      return item
    })

    return items
  }, [])

  /**
   * キーワード検索時の処理
   */
  const searchItems = useCallback(async (keyword: string) => {
    if (keyword === "") {
      // キーワードクリア時
      if (currentMenu === MenuItemType.FACILITY) {
        return getFacilityItems()
      } else if (currentMenu === MenuItemType.CATEGORY) {
        return getCategoryItems()
      } else if (currentMenu === MenuItemType.SEAT) {
        return getSeatItems()
      }
    }

    let items: Item[] = []
    if (currentMenu === MenuItemType.FACILITY ||
        currentMenu === MenuItemType.CATEGORY) {
      // Mappedin APIを使用してキーワード検索を行い結果を全件取得します
      const hits = await mapClient.search(keyword)
      items = hits.map(hit => {
        const item: Item = {
          label: hit.name,
          value: hit.id,
        }
        return item
      })
    } else if (currentMenu === MenuItemType.SEAT) {
      // 座席情報を絞り込みます
      items = mapClient.getLocations().filter(location => {
        return (location.type === "seating") &&
          (location.name.indexOf(keyword) >= 0)
      }).map((location) => {
        const item: Item = {
          label: location.name,
          value: location.id,
        }
        return item
      })
    }

    return items
  }, [currentMenu, getCategoryItems, getFacilityItems, getSeatItems])

  /**
   * メニュー変更時の処理
   */
  useEffect(() => {
    // 検索キーワードをクリア
    setKeyword("")

    // 変更後のメニューに従いリストの内容を取得して設定する
    switch (currentMenu) {
      case MenuItemType.FACILITY: {
        setItems(getFacilityItems())
        break
      }
      case MenuItemType.CATEGORY: {
        setItems(getCategoryItems())
        break
      }
      case MenuItemType.SEAT: {
        setItems(getSeatItems())
        break
      }
    }

    const el = document.getElementById("search")
    if (el) {
      // inputイベントからobserverを生成
      const observer = fromEvent<InputEvent>(el, "input")

      if (inputSubscriber) {
        // 以前のsubscribeをキャンセルします
        inputSubscriber.unsubscribe()
      }
      // キーの入力から最低でも100ms待ってから通知します
      // 前の値が待機中の場合は破棄されます
      inputSubscriber = observer
        .pipe(debounceTime(100))
        .pipe(map((element: InputEvent) => (element.target as HTMLInputElement).value))
        .pipe(switchMap((keyword: string) => searchItems(keyword)))
        .subscribe((items: Item[]) => setItems(items))
    }
  }, [currentMenu, selectedCategory, getSeatItems, getFacilityItems, searchItems, getCategoryItems])

  /**
   * リストの戻るアイコン押下時の処理
   */
  const onBackClick = useCallback(() => {
    if (currentMenu === MenuItemType.FACILITY) {
      setSelectedCategory(null)
      setCurrentMenu(MenuItemType.CATEGORY)
    }
  }, [currentMenu, setCurrentMenu, setSelectedCategory])

  /**
   * リストのアイテム選択時の処理
   * @param item 選択したアイテム
   */
  const onListItemClick = useCallback((item: Item) => {
    if (currentMenu === MenuItemType.CATEGORY) {
      setSelectedCategory(item.value)
      setCurrentMenu(MenuItemType.FACILITY)
    }

    // ロケーションをマップで選択状態にする
    try {
      mapClient.focusOnLocation(item.value)
      setCurrentLocation(item.value)
    } catch (e) {
      // ロケーションが選択できない場合はエラーになるがここでは何もしない
    }

    // フロアが移動する可能性もあるため階層情報を更新する
    const level = mapClient.getCurrentMapLevel()
    setCurrentLevel(level)
  }, [currentMenu, setCurrentLevel, setCurrentLocation, setCurrentMenu, setSelectedCategory])

  /**
   * 閉じるアイコン押下時の処理
   */
  const onCloseClick = () => {
    if (currentMenu === MenuItemType.ROUTE) {
      // 案内メニューを閉じる際はマップをクリアする
      mapClient.clearRouteSearchResult()
    }
    setKeyword("")
    setCurrentMenu(null)
    setSelectedCategory(null)
    setItems([])
  }

  /**
   * メニュー選択時の処理
   * @param item 選択されたメニュー
   */
  const onMenuItemClick = (item: MenuItemType) => {
    if (currentMenu === MenuItemType.ROUTE &&
        item !== MenuItemType.ROUTE) {
      // 案内メニューから離れる際はマップをクリアする
      mapClient.clearRouteSearchResult()
    }
    setItems([])
    setCurrentMenu(item)
  }

  /**
   * リストの内容を表示
   */
  const renderItemList = () => {
    let textLabel = "施設を検索"
    if (currentMenu === MenuItemType.SEAT) {
      textLabel = "座席を検索"
    }
    return (
      <>
        <ItemList
          items={items}
          onItemClick={onListItemClick}
          onEmptyClick={onBackClick}
        />
        <div className={style.search}>
          <TextField
            id={"search"}
            label={textLabel}
            value={keyword}
            fullWidth
            variant="outlined"
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
      </>
    )
  }

  return (
    <div className={style.frame}>
      {selectedLocation && (
        <div className={style.locationCardContainer}>
          <LocationCard location={selectedLocation} />
        </div>
      )}
      {currentMenu && (
        <div className={style.panel}>
          <div className={style.header}>
            {currentMenu === MenuItemType.FACILITY && (
              <IconButton className={style.back} onClick={onBackClick}>
                <ArrowLeftIcon />
              </IconButton>
            )}
            <IconButton className={style.close} onClick={() => onCloseClick()}>
              <CloseIcon />
            </IconButton>
          </div>
          {currentMenu && currentMenu !== MenuItemType.ROUTE && renderItemList()}
          {currentMenu === MenuItemType.ROUTE && (<Navigator />)}
        </div>
      )}
      <Menu onItemClick={onMenuItemClick} selectedItem={currentMenu}/>
    </div>
  )
}

export default connector(ControlBoard)
